import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserSpeechController, browserSpeechConstructor } from "./browser-speech";
import { LocalSpeech } from "./local-speech";
import { PcmRecorder } from "./pcm-recorder";

type VoiceState = "idle" | "requesting" | "recording" | "transcribing" | "recognizing";
const stopTracks = (stream: MediaStream | null) => stream?.getTracks().forEach((track) => track.stop());

/** Browser recognition is primary. Local Whisper remains an explicit retry and the unsupported-browser fallback. */
export function useDowntimeVoiceEntry(key: string, onTranscript: (text: string) => void, onBusyChange?: (busy: boolean) => void) {
  const [state, setState] = useState<VoiceState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [interim, setInterim] = useState("");
  const live = useRef(true), sequence = useRef(0), phase = useRef<VoiceState>("idle");
  const recorder = useRef<PcmRecorder | null>(null), stream = useRef<MediaStream | null>(null);
  const controller = useRef<AbortController | null>(null), timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const browser = useRef<BrowserSpeechController | null>(null), speech = useRef(new LocalSpeech()), callback = useRef(onTranscript);
  callback.current = onTranscript;
  const setBusy = (busy: boolean) => onBusyChange?.(busy);
  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const releaseRecording = () => { clearTimer(); recorder.current?.dispose(); recorder.current = null; stopTracks(stream.current); stream.current = null; };
  const idle = () => { phase.current = "idle"; setBusy(false); if (live.current) setState("idle"); };
  const cancel = useCallback(() => {
    sequence.current += 1; phase.current = "idle"; clearTimer(); setBusy(false);
    browser.current?.cancel(); browser.current = null; controller.current?.abort(); controller.current = null;
    releaseRecording(); speech.current.dispose();
    if (live.current) { setState("idle"); setMessage(null); setInterim(""); }
  }, []);

  const stopLocal = useCallback(async () => {
    if (phase.current !== "recording" || !recorder.current) return;
    const current = sequence.current; phase.current = "transcribing"; clearTimer(); setState("transcribing"); setMessage("Ses bu cihazda yazıya çevriliyor…");
    const activeRecorder = recorder.current;
    try {
      const audio = await activeRecorder.stop();
      if (current !== sequence.current || !live.current) return;
      recorder.current = null; stopTracks(stream.current); stream.current = null;
      const request = new AbortController(); controller.current = request;
      const text = await speech.current.transcribe(audio, request.signal, (progress) => { if (current === sequence.current && live.current) setMessage(progress); });
      if (current === sequence.current && live.current && !request.signal.aborted) callback.current(text);
      if (current === sequence.current) { idle(); setMessage(null); }
    } catch (error) {
      if (current === sequence.current) { idle(); setMessage(error instanceof Error ? error.message : "Ses kaydı işlenemedi. Klavyeyle devam edebilirsiniz."); }
    } finally { if (current === sequence.current) { controller.current = null; releaseRecording(); } }
  }, []);

  const startLocal = useCallback(async () => {
    if (phase.current !== "idle") return;
    if (!navigator.mediaDevices?.getUserMedia || typeof AudioContext === "undefined" || typeof AudioWorkletNode === "undefined") { setMessage("Bu cihaz yerel ses kaydını desteklemiyor. Klavyeyle neden girebilirsiniz."); return; }
    const current = ++sequence.current; phase.current = "requesting"; setBusy(true); setState("requesting"); setMessage("Mikrofon izni bekleniyor…"); setInterim("");
    try {
      const nextStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (current !== sequence.current || !live.current) { stopTracks(nextStream); return; }
      stream.current = nextStream; const nextRecorder = new PcmRecorder(); recorder.current = nextRecorder;
      await nextRecorder.start(nextStream);
      if (current !== sequence.current || !live.current) { nextRecorder.dispose(); stopTracks(nextStream); return; }
      phase.current = "recording"; setState("recording"); setMessage("Yerel ses kaydı dinleniyor… Konuşmayı bitirince durdurun.");
      timer.current = setTimeout(() => { if (sequence.current === current) void stopLocal(); }, 45000);
    } catch (error) {
      if (current === sequence.current) { releaseRecording(); idle(); setMessage(error instanceof Error && error.name === "NotAllowedError" ? "Mikrofon izni verilmedi. Klavyeyle neden girebilirsiniz." : "Mikrofon başlatılamadı. Klavyeyle neden girebilirsiniz."); }
    }
  }, [stopLocal]);

  const start = useCallback(() => {
    if (phase.current !== "idle") return;
    const Constructor = browserSpeechConstructor();
    if (!Constructor) { void startLocal(); return; }
    const current = ++sequence.current; phase.current = "recognizing"; setBusy(true); setState("recognizing"); setMessage("Konuşun…"); setInterim("");
    const complete = (text: string) => { if (current !== sequence.current || !live.current) return; browser.current = null; idle(); setMessage(null); setInterim(""); callback.current(text); };
    const failure = (text: string) => { if (current !== sequence.current || !live.current) return; browser.current = null; idle(); setMessage(text); setInterim(""); };
    const recognition = new BrowserSpeechController(Constructor, { interim: (text) => { if (current === sequence.current && live.current) setInterim(text); }, final: complete, failure });
    browser.current = recognition; recognition.start();
  }, [startLocal]);
  const stop = useCallback(() => {
    if (phase.current === "recognizing") browser.current?.stop();
    else void stopLocal();
  }, [stopLocal]);
  useEffect(() => { live.current = true; cancel(); return () => { live.current = false; cancel(); }; }, [key, cancel]);
  return { state, message, interim, start, startLocal, stop, cancel };
}
